import { FC, useState } from 'react'
import { Box, Button, IconButton, MenuItem, Select, Stack, TextField } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined'
import AddIcon from '@mui/icons-material/Add'
import {
  GridColDef,
  GridColType,
  GridFilterOperator,
  GridLogicOperator,
  getGridBooleanOperators,
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
} from '@mui/x-data-grid'
import { FilterItem, WorklistFilterPreference } from './preferencesSlice'

interface WorklistFilterPanelProps {
  columns: GridColDef[]
  model: WorklistFilterPreference
  onApply: (model: WorklistFilterPreference) => void
  onClose?: () => void
}

// Community DataGrid allows only a single filter condition, so the Worklist
// drives multi-condition filtering (AND/OR) through this panel instead of the
// grid's built-in filter UI. It emits the same {linkOperator, items} model the
// Redux/URL/server pipeline already consumes, so nothing downstream changes.

// Default operator set by column type, mirroring how the grid resolves
// operators for a typed column. Without this, a `date` column with no explicit
// `filterOperators` (e.g. createdAt) would fall back to string operators and a
// text input, sending a nonsensical operator to the backend.
const defaultOperatorsForType = (type: GridColType | undefined): readonly GridFilterOperator[] => {
  switch (type) {
    case 'date':
      return getGridDateOperators(false)
    case 'dateTime':
      return getGridDateOperators(true)
    case 'number':
      return getGridNumericOperators()
    case 'boolean':
      return getGridBooleanOperators()
    case 'singleSelect':
      return getGridSingleSelectOperators()
    default:
      return getGridStringOperators()
  }
}

const columnOperators = (column: GridColDef | undefined): readonly GridFilterOperator[] =>
  column?.filterOperators && column.filterOperators.length > 0
    ? column.filterOperators
    : defaultOperatorsForType(column?.type)

// Standard MUI operators and most of the custom date operators carry no `label`
// (the grid normally derives it from localeText), so humanize the internal
// value ("onOrAfter" -> "on or after") instead of showing the raw token.
const humanizeOperator = (value: string): string =>
  value
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .toLowerCase()

const operatorLabel = (operator: GridFilterOperator): string =>
  operator.label ?? humanizeOperator(operator.value)

// A filter value is only collected when the chosen operator declares an input
// component (e.g. `is empty` / `is overdue` operators take no value).
const operatorTakesValue = (operator: GridFilterOperator | undefined): boolean =>
  operator?.InputComponent != null

const firstOperatorValue = (column: GridColDef | undefined): string =>
  columnOperators(column)[0]?.value ?? 'contains'

export const WorklistFilterPanel: FC<WorklistFilterPanelProps> = ({
  columns,
  model,
  onApply,
  onClose,
}) => {
  const filterableColumns = columns.filter(column => column.filterable !== false && !!column.field)
  const columnByField = Object.fromEntries(filterableColumns.map(column => [column.field, column]))

  const [items, setItems] = useState<FilterItem[]>(model.items)
  const [linkOperator, setLinkOperator] = useState<string>(
    model.linkOperator || GridLogicOperator.And,
  )

  const updateItem = (index: number, patch: Partial<FilterItem>) => {
    setItems(current => current.map((item, i) => (i === index ? { ...item, ...patch } : item)))
  }

  const handleFieldChange = (index: number, field: string) => {
    const column = columnByField[field]
    updateItem(index, { field, operator: firstOperatorValue(column), value: '' })
  }

  const handleOperatorChange = (index: number, operator: string) => {
    const item = items[index]
    if (!item) return
    const column = columnByField[item.field]
    const takesValue = operatorTakesValue(columnOperators(column).find(o => o.value === operator))
    updateItem(index, { operator, value: takesValue ? item.value : '' })
  }

  const addCondition = () => {
    const column = filterableColumns[0]
    if (!column?.field) {
      return
    }
    setItems(current => [
      ...current,
      { field: column.field, operator: firstOperatorValue(column), value: '' },
    ])
  }

  const removeCondition = (index: number) => {
    setItems(current => current.filter((_, i) => i !== index))
  }

  const apply = () => {
    onApply({
      linkOperator,
      items: items.filter(item => !!item.field && !!item.operator),
    })
    onClose?.()
  }

  const clearAll = () => {
    onApply({ linkOperator: GridLogicOperator.And, items: [] })
    onClose?.()
  }

  return (
    <Box sx={{ p: 2, minWidth: 480 }}>
      <Stack spacing={1.5}>
        {items.length === 0 && (
          <Box sx={{ color: 'text.secondary', fontSize: 14 }}>No filter conditions.</Box>
        )}
        {items.map((item, index) => {
          const column = columnByField[item.field]
          const operators = columnOperators(column)
          const operator = operators.find(o => o.value === item.operator)
          return (
            <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: 80, fontSize: 13, color: 'text.secondary' }}>
                {index === 0 ? (
                  'Where'
                ) : index === 1 ? (
                  <Select
                    size="small"
                    value={linkOperator}
                    onChange={e => setLinkOperator(e.target.value)}
                    inputProps={{ 'aria-label': 'Combine conditions with' }}
                    fullWidth
                  >
                    <MenuItem value={GridLogicOperator.And}>And</MenuItem>
                    <MenuItem value={GridLogicOperator.Or}>Or</MenuItem>
                  </Select>
                ) : (
                  linkOperator.toLowerCase()
                )}
              </Box>
              <Select
                size="small"
                value={columnByField[item.field] ? item.field : ''}
                onChange={e => handleFieldChange(index, e.target.value)}
                inputProps={{ 'aria-label': `Condition ${index + 1} field` }}
                sx={{ minWidth: 150 }}
              >
                {filterableColumns.map(column => (
                  <MenuItem key={column.field} value={column.field}>
                    {column.headerName ?? column.field}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={operator ? item.operator : ''}
                onChange={e => handleOperatorChange(index, e.target.value)}
                inputProps={{ 'aria-label': `Condition ${index + 1} operator` }}
                sx={{ minWidth: 130 }}
              >
                {operators.map(op => (
                  <MenuItem key={op.value} value={op.value}>
                    {operatorLabel(op)}
                  </MenuItem>
                ))}
              </Select>
              {/* Only text and date value inputs are handled — the Worklist has
                  no boolean/singleSelect columns today. If one is added, this
                  needs the option-picker / Yes-No input (GridFilterInputSingleSelect
                  / GridFilterInputBoolean, or reuse the operator's InputComponent)
                  instead of a free-text box. */}
              {operatorTakesValue(operator) && (
                <TextField
                  size="small"
                  type={column?.type === 'date' ? 'date' : 'text'}
                  value={item.value}
                  onChange={e => updateItem(index, { value: e.target.value })}
                  sx={{ minWidth: 150 }}
                  slotProps={{
                    htmlInput: { 'aria-label': `Condition ${index + 1} value` },
                    inputLabel: column?.type === 'date' ? { shrink: true } : undefined,
                  }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => removeCondition(index)}
                aria-label={`Remove condition ${index + 1}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          )
        })}
      </Stack>
      <Stack direction="row" sx={{ mt: 2, justifyContent: 'space-between' }}>
        <Button startIcon={<AddIcon />} onClick={addCondition} size="small">
          Add condition
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={clearAll} size="small" color="inherit">
            Clear all
          </Button>
          <Button onClick={apply} size="small" variant="contained">
            Apply
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
